import apiClient from '../client';
import { APIResponse, ContentItem } from '@memriti/shared';

export interface PracticeSession {
  id: number;
  user_id: number;
  start_time: string;
  end_time: string | null;
  status: 'in_progress' | 'completed';
  correct_items: number;
  total_time: number;
  duration_minutes: number;
  session_items: PracticeSessionItem[];
  practice_session_items?: PracticeSessionItem[]; // Backend alias field
}

export interface PracticeSessionItem {
  id: number;
  session_id: number;
  content_id: number;
  content_type: string;
  user_answer: string | null;
  is_correct: boolean | null;
  time_spent: number;
  performance_rating: number;
  review_date: string | null;
  content: ContentItem | null;
  reason?: string;
}

export interface PracticeProgress {
  id: number;
  user_id: number;
  content_id: number;
  content_type: string;
  microtopic_id: number;
  last_review: string | null;
  next_review: string | null;
  ease_factor: number;
  interval: number;
  repetitions: number;
  mastery_level: number;
}

export interface UpdateSessionItemResponse {
  updated_item: PracticeSessionItem;
  next_item: PracticeSessionItem | null;
}

export interface StartSessionResponse {
  session: PracticeSession;
  first_item: PracticeSessionItem;
}

export interface SessionStatistics {
  session_id: number;
  total_questions: number;
  unique_questions: number;
  repeated_questions: number;
  max_repetitions: number;
  repetition_rate: number;
  by_reason: Record<string, number>;
  by_content_type: Record<string, number>;
  question_breakdown: {
    asked_once: number;
    asked_twice: number;
    asked_thrice_plus: number;
  };
}

export const practiceService = {
  async startSession(duration_minutes: number, subtopic_ids?: number[], company_tag_ids?: number[]): Promise<APIResponse<StartSessionResponse>> {
    const response = await apiClient.post('/api/v1/practice/sessions', {
      duration_minutes,
      subtopic_ids,
      company_tag_ids
    });
    return response.data;
  },

  async getNextItem(sessionId: number, company_tag_ids?: number[]): Promise<APIResponse<PracticeSessionItem | null>> {
    const params = new URLSearchParams();
    if (company_tag_ids && company_tag_ids.length > 0) {
      params.append('company_tag_ids', company_tag_ids.join(','));
    }

    const response = await apiClient.get(`/api/v1/practice/sessions/${sessionId}/next-item?${params.toString()}`);
    return response.data;
  },

  async completeSession(sessionId: number): Promise<APIResponse<PracticeSession>> {
    const response = await apiClient.post(`/api/v1/practice/sessions/${sessionId}/complete`);
    return response.data;
  },

  async updateSessionItem(
    sessionId: number,
    itemId: number,
    userAnswer: string,
    timeSpent: number
  ): Promise<APIResponse<PracticeSessionItem>> {
    const response = await apiClient.put(`/api/v1/practice/sessions/${sessionId}/items/${itemId}`, {
      user_answer: userAnswer,
      time_spent: timeSpent
    });
    return response.data;
  },

  async getSessionStatistics(sessionId: number): Promise<APIResponse<SessionStatistics>> {
    const response = await apiClient.get(`/api/v1/practice/sessions/${sessionId}/statistics`);
    return response.data;
  },

  async getSimilarQuestions(params: {
    topic: string;
    subtopic: string;
    microtopic: string;
    content_type: string;
    content_id?: number;
    count?: number;
  }): Promise<APIResponse<any>> {
    const response = await apiClient.post('/api/v1/practice/similar-questions', params);
    return response.data;
  },

  async getAIExplanation(params: {
    content_id: number;
    content_type: string;
  }): Promise<APIResponse<any>> {
    const response = await apiClient.post('/api/v1/practice/ai-explanation', params);
    return response.data;
  },
};

export const updatePracticeProgress = async (
  progressId: number,
  data: Partial<PracticeProgress>
): Promise<APIResponse<PracticeProgress>> => {
  const response = await apiClient.put(`/api/v1/practice/progress/${progressId}`, data);
  return response.data;
};
